import React, { ReactNode, memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiIcon } from "@miplatform/components";
import {
  ACTIVITY_CARD_CONFIG,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { IActivity } from "@miplatform/types";
import { mdiCodeBrackets } from "@mdi/js";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { EmailActivityPreviewModal } from "../EmailActivityPreviewModal/EmailActivityPreviewModal";

export const ActivityCard = memo<ActivityCardProps>(
  ({ activity, showOrderTemplate = false, showCardBorder, onClick }) => {
    const theme = useTheme();
    const {
      id,
      activityType,
      createdByUser,
      reason,
      notes,
      entityAfter,
      orderTemplateName = "",
    } = activity;

    const {
      icon,
      iconBgColor = theme.palette.info.main,
      bgColor = theme.palette.common.white,
      title,
    } = ACTIVITY_CARD_CONFIG[activityType] || {
      icon: mdiCodeBrackets,
      iconColor: theme.palette.common.white,
      iconBgColor: theme.palette.info.main,
    };

    const { classes } = useStyles({
      bgColor,
      iconBgColor,
      showCardBorder,
      useHover: Boolean(onClick),
    });

    const [isEmailModalOpen, openEmailModal, closeEmailModal] = useBoolean();
    const shouldRenderEmailModal = activityType === "EmailNotification";
    const shouldShowPriceChangeDetails = activityType === "PriceLevelUpdated";

    const parseHighlightedText = (
      text = "",
      highlightColor = "info.main"
    ): ReactNode[] => {
      if (!text) return [];

      const parts = text.split(/(<highlight>|<\/highlight>)/);

      return parts.map((part, index) => {
        if (part === "<highlight>") {
          return null;
        }

        if (part === "</highlight>") {
          return null;
        }

        const isBold = index > 0 && parts[index - 1] === "<highlight>";

        if (isBold) {
          return (
            <Typography
              key={index}
              component="span"
              fontSize="inherit"
              color={highlightColor}
              fontWeight={theme.fontWeight.semiBold}
            >
              {part}
            </Typography>
          );
        }

        return part;
      });
    };

    const headerInfo = (
      showOrderTemplate
        ? [
            orderTemplateName,
            createdByUser?.displayName,
            createdByUser?.tenantName,
          ]
        : [createdByUser?.displayName, createdByUser?.tenantName]
    ).filter(Boolean);
    const info = parseHighlightedText(notes || reason).filter(Boolean);

    const priceChangeDetails = useMemo(() => {
      if (!shouldShowPriceChangeDetails || !entityAfter) return [];
      try {
        const parsed = JSON.parse(entityAfter);
        return Array.isArray(parsed) &&
          parsed.every((line) => typeof line === "string")
          ? parsed
          : [];
      } catch {
        return [];
      }
    }, [shouldShowPriceChangeDetails, entityAfter]);

    const renderHeaderInfo = () => {
      return headerInfo.map((item, index) => (
        <Box key={item + id} display="flex" alignItems="center" gap={1}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY1}
            color={index === 0 ? "info.main" : "grey.medium"}
            fontSize={theme.fontSize.small}
            fontWeight={
              index === 0 ? theme.fontWeight.bold : theme.fontWeight.regular
            }
          >
            {item}
          </Typography>
          {index < headerInfo.length - 1 && (
            <Divider orientation="vertical" style={{ height: 10 }} />
          )}
        </Box>
      ));
    };

    return (
      <>
        <Box
          className={classes.activityCard}
          alignItems="center"
          onClick={() => {
            if (onClick) {
              onClick(activity);
            }
          }}
        >
          <Box display="flex" flexDirection="column" gap={1} flex={1}>
            <Box display="flex" alignItems="center" gap={1}>
              {renderHeaderInfo()}
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Box className={classes.iconContainer}>
                <MiIcon
                  path={icon}
                  size={0.5}
                  color={theme.palette.common.white}
                />
              </Box>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.BODY1}
                fontSize={theme.fontSize.small}
                fontWeight={theme.fontWeight.semiBold}
              >
                {`${title || activityType}${info.length > 0 ? ": " : ""} `}
                <Typography
                  component="span"
                  fontSize="inherit"
                  color="grey.dark"
                >
                  {info}
                </Typography>
              </Typography>
            </Box>
            {reason && (
              <Box className={classes.reasonBox}>
                <Typography className={classes.reasonLabel}>Notes:</Typography>
                <Typography className={classes.reasonText}>
                  {parseHighlightedText(reason, "text.primary")}
                </Typography>
              </Box>
            )}
            {priceChangeDetails.length > 0 && (
              <Box className={classes.priceChangeDetailsBox}>
                {priceChangeDetails.map((line, index) => (
                  <Typography
                    key={index}
                    className={classes.priceChangeDetailsText}
                  >
                    {parseHighlightedText(line, "text.primary")}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
          {shouldRenderEmailModal && (
            <MiButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openEmailModal();
              }}
              variant="outlined"
              data-testid="view-email-activity-button"
            >
              View
            </MiButton>
          )}
        </Box>
        {shouldRenderEmailModal && (
          <EmailActivityPreviewModal
            activity={activity}
            isEmailModalOpen={isEmailModalOpen}
            closeEmailModal={closeEmailModal}
          />
        )}
      </>
    );
  }
);

const useStyles = makeStyles<StyleProps>()(
  (theme, { bgColor, iconBgColor, showCardBorder, useHover }) => ({
    activityCard: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      borderRadius: 4,
      gap: theme.spacing(1),
      padding: theme.spacing(2),
      backgroundColor: bgColor,
      cursor: useHover ? "pointer" : "default",
      transition: "border 0.1s ease",
      border: `1px solid ${
        showCardBorder ? theme.palette.grey.accent : "transparent"
      }`,
      "&:hover": {
        border: useHover
          ? `1px solid ${theme.palette.primary.main}`
          : "1px solid transparent",
      },
    },
    iconContainer: {
      minWidth: 18,
      height: 18,
      borderRadius: "50%",
      backgroundColor: iconBgColor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    reasonBox: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      backgroundColor: theme.palette.grey.light,
      padding: theme.spacing(0, 1),
      borderRadius: 4,
      border: `1px solid ${theme.palette.grey.accent}`,
    },
    reasonLabel: {
      fontWeight: theme.fontWeight.semiBold,
      fontSize: theme.fontSize.small,
    },
    reasonText: {
      fontSize: theme.fontSize.small,
      color: theme.palette.grey.medium,
    },
    priceChangeDetailsBox: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.5),
      backgroundColor: theme.palette.grey.light,
      padding: theme.spacing(1),
      borderRadius: 4,
      border: `1px solid ${theme.palette.grey.accent}`,
    },
    priceChangeDetailsText: {
      fontSize: theme.fontSize.small,
      color: theme.palette.grey.medium,
    },
  })
);

interface ActivityCardProps {
  activity: IActivity;
  showOrderTemplate?: boolean;
  showCardBorder?: boolean;
  onClick?: (activity: IActivity) => void;
}
interface StyleProps {
  bgColor: string;
  iconBgColor: string;
  showCardBorder?: boolean;
  useHover: boolean;
}
